import React, { useCallback, useEffect, useState } from 'react';
import { Button, FilePicker, Heading, IconButton, Pane, Table, Text, toaster } from 'evergreen-ui';
import Request from '../../../util/Api';

const formatSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function StockOperationDocuments({ stockOperationId }) {
    const [documents, setDocuments] = useState([]);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    // bumping the key remounts the FilePicker so it clears after an upload
    const [pickerKey, setPickerKey] = useState(0);

    const loadDocuments = useCallback(async () => {
        try {
            const res = await Request.get(`/stock-operations/${stockOperationId}/documents`);
            setDocuments((res && res.data) || []);
        } catch (err) {
            toaster.danger('Nu am putut incarca documentele');
        }
    }, [stockOperationId]);

    useEffect(() => {
        if (stockOperationId) {
            loadDocuments();
        }
    }, [stockOperationId, loadDocuments]);

    const handleUpload = async () => {
        if (!files.length) {
            toaster.warning('Alege cel putin un fisier');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            files.forEach((file) => formData.append('file', file));

            await Request.post(`/stock-operations/${stockOperationId}/documents`, null, {}, formData);

            toaster.success(files.length > 1 ? 'Documente incarcate' : 'Document incarcat');
            setFiles([]);
            setPickerKey((k) => k + 1);
            await loadDocuments();
        } catch (err) {
            toaster.danger('Incarcarea a esuat: ' + (err.message || 'eroare necunoscuta'));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (documentId) => {
        try {
            await Request.delete(`/stock-operations/${stockOperationId}/documents/${documentId}`);
            toaster.success('Document sters');
            await loadDocuments();
        } catch (err) {
            toaster.danger('Stergerea a esuat: ' + (err.message || 'eroare necunoscuta'));
        }
    };

    return (
        <Pane marginTop={20} marginBottom={20}>
            <Heading marginBottom={12}>Documente</Heading>

            <Pane display="flex" alignItems="center" marginBottom={12}>
                <FilePicker
                    key={pickerKey}
                    multiple
                    width={360}
                    marginRight={12}
                    onChange={(picked) => setFiles(Array.from(picked || []))}
                />
                <Button
                    appearance="primary"
                    iconBefore="upload"
                    isLoading={uploading}
                    disabled={!files.length}
                    onClick={handleUpload}
                >
                    Incarca
                </Button>
            </Pane>

            {documents.length === 0 ? (
                <Text color="muted">Nu exista documente atasate.</Text>
            ) : (
                <Table>
                    <Table.Head>
                        <Table.TextHeaderCell>Nume</Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={120} flexShrink={0} flexGrow={0}>Marime</Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={80} flexShrink={0} flexGrow={0}>Actiuni</Table.TextHeaderCell>
                    </Table.Head>
                    <Table.Body>
                        {documents.map((doc) => (
                            <Table.Row key={doc.id}>
                                <Table.TextCell>
                                    <a href={doc.url} target="_blank" rel="noreferrer">{doc.originalName}</a>
                                </Table.TextCell>
                                <Table.TextCell flexBasis={120} flexShrink={0} flexGrow={0}>
                                    {formatSize(doc.size)}
                                </Table.TextCell>
                                <Table.Cell flexBasis={80} flexShrink={0} flexGrow={0}>
                                    <IconButton icon="trash" intent="danger" onClick={() => handleDelete(doc.id)} />
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
        </Pane>
    );
}
